
  import { Vue, Component, Prop } from "vue-property-decorator";

  import UPDATE_USER_APPLICATION from "@/apollo/jobboard/UpdateUserApplication.gql";

  import CardBodyItem from "@/components/card/CardBodyItem.vue";
  import Card from "@/components/card/Card.vue";
  import UIButton from "@/components/button/ui-button.vue";
  import CollapsibleDescription from "@/components/common/CollapsibleDescription.vue";
  import Chip from "@/components/filters/Chip.vue";
  import TableAvatar from "@/components/table/TableAvatar.vue";
  import ItemChipsWithMore from "@/components/common/ItemChipsWithMore.vue";
  import CircleWithInitials from "@/components/card/CircleWithInitials.vue";
  import Badge from "@/components/common/Badge.vue";
  import Tooltip from "@/components/common/Tooltip.vue";

  import { getSectionRouteName } from "@/helpers/routes/getSectionRouteName";

  import { Application_Set_Input, User, Document, Posting_Industry, Posting_Skill } from "@/generated/graphql";
  import RouteNames from "@/entities/RouteNames";
  import BadgeColor from "@/entities/BadgeColor";
  import { ApplicantStatus } from "@/entities/ApplicantStatus";
  import { NOTIFICATION } from "@/entities/Events";
  import Message from "@/data/notificationsMessages";
  import RouteSectionsPrefixes from "@/entities/RouteSectionsPrefixes";
  import getFullName from "@/helpers/getFullName";
  import ApplicationNote from "@/components/jobboard/ApplicationNote.vue";
  import ItemChipsData from "@/entities/ItemChipsData";
  import ColorIconContainer from "@/components/icons/ColorIconContainer.vue";
  import DownloadIcon from "@/components/icons/DownloadIcon.vue";
  import DocumentProvider from "@/components/documents/DocumentProvider.vue";
  import LinkIcon from "@/components/icons/LinkIcon.vue";
  import ApplicationAttachment from "@/components/jobboard/ApplicationAttachment.vue";
  import DetachIcon from "@/components/icons/DetachIcon.vue";
  import getFormattedDuration from "@/helpers/getFormattedDuration";

  @Component({
    components: {
      DetachIcon,
      ApplicationAttachment,
      LinkIcon,
      DocumentProvider,
      DownloadIcon,
      ColorIconContainer,
      ApplicationNote,
      UIButton,
      CardBodyItem,
      Card,
      CollapsibleDescription,
      Chip,
      TableAvatar,
      ItemChipsWithMore,
      CircleWithInitials,
      Badge,
      Tooltip,
    },
  })
  export default class JobBoardApplicationCard extends Vue {
    @Prop({ type: String, required: true }) readonly currentUserId!: string;
    @Prop({ type: String, required: true }) readonly currentUserRole!: string;
    @Prop({ type: String, required: true }) readonly applicationId!: string;
    @Prop({ type: String, required: true }) readonly postingId!: string;
    @Prop({ type: String, required: true }) readonly jobTitle!: string;
    @Prop({ type: String, required: true }) readonly companyName!: string;
    @Prop({ type: String, required: true }) readonly postingCompanyId!: string;
    @Prop({ type: Boolean, default: false }) readonly postingActive!: boolean;
    @Prop({ type: Number, default: undefined }) readonly duration?: number;
    @Prop({ type: String, default: undefined }) readonly employmentType?: string;
    @Prop({ type: String, default: undefined }) readonly note?: string;
    @Prop({ type: String, default: undefined }) readonly location?: string;
    @Prop({ type: String, required: true }) readonly createdAt!: string;
    @Prop({ type: String, default: undefined }) readonly description!: string;
    @Prop({ type: Array, required: true }) readonly skills!: Posting_Skill[];
    @Prop({ type: Array, required: true }) readonly industries!: Posting_Industry[];
    @Prop({ type: String, default: undefined }) readonly companyLogo!: string;
    @Prop({ type: Boolean, default: false }) readonly isCancelled!: boolean;
    @Prop({ type: String, default: "" }) readonly interviewedBy!: string;
    @Prop({ type: Object, default: undefined }) readonly interviewer?: User;
    @Prop({ type: Object, default: undefined }) readonly attachedDocument?: Document;

    routes = RouteNames;
    processing: boolean = false;
    BadgeColor: typeof BadgeColor = BadgeColor;

    get applicationDetailsRouteName(): string {
      return getSectionRouteName({
        routeName: RouteNames.JOBBOARD_POSTING,
        prefix: RouteSectionsPrefixes.APPLICATIONS,
      });
    }

    get attachedDocumentName(): string {
      return this.attachedDocument?.filename || "";
    }

    get attachedDocumentId(): string | undefined {
      return this.attachedDocument?.id;
    }

    get isSkillsExist(): boolean {
      return this.skills.length > 0;
    }

    get isCommentExists(): boolean {
      return Boolean(this.attachedDocument || this.note);
    }

    get isCanceledStatus(): boolean {
      return this.isCancelled;
    }

    get formattedCreatedAt(): string {
      return this.$moment(this.createdAt).format("D MMM, YYYY");
    }

    get navigateToDetails(): Object {
      return {
        name: RouteNames.JOBBOARD_POSTING,
        params: { id: this.postingId },
      };
    }

    get industriesData(): ItemChipsData[] {
      return this.industries.map(({industry}) => ({
        id: industry.id,
        name: industry.name,
      }));
    }
    
    get postingDuration(): string | undefined {
      return getFormattedDuration(this.duration);
    }

    get interviewTooltipText(): string {
      const { given_name: name, family_name: surname } = this.interviewer!;
      const interviewerName = getFullName(name!, surname!);
      return `For more details find a conversation with ${interviewerName} in your ProveraCare Inbox`;
    }

    get skillsData(): any[] {
      if (this.skills?.length) {
        return this.skills.reduce<ItemChipsData[]>((acc, { skill }) => {
          if (skill.name) {
            acc.push({ name: skill.name, id: skill.id });
          }
          return acc;
        }, []);
      }
      return [];
    }

    async revokeApplication(): Promise<void> {
      const set: Application_Set_Input = {
          interviewed_by: null,
          is_cancelled: true,
          document_id: null,
          note: null,
        }

      try {
        this.processing = true;
        await this.updateApplication(set);
        this.$emit('refetchAfterDeletingApplication');
      } catch (e) {
        this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
      } finally {
        this.processing = false;
      }
    }

    async updateApplication(set: Application_Set_Input): Promise<void> {
      await this.$apollo.mutate({
        mutation: UPDATE_USER_APPLICATION,
        variables: {
          where: {
            id: { _eq: this.applicationId },
          },
          set,
        },
      });
    }

    detachDocument() {
      this.$confirm({
        title: `Detach document from ${this.jobTitle} application`,
        message: `Are you sure?`,
        button: {
          no: "No",
          yes: "Yes",
        },
        callback: async (confirm: any) => {
          if (confirm) {
            this.$emit('detachDocument', this.applicationId);
          }
        },
      });
    }

    removeComments() {
      this.$confirm({
        title: `Remove comments and attachments from ${this.jobTitle} application`,
        message: `Are you sure?`,
        button: {
          no: "No",
          yes: "Yes",
        },
        callback: async (confirm: any) => {
          if (confirm) {
            this.$emit('removeComments', this.applicationId);
          }
        },
      });
    }
  }
